import { EpicDeps } from 'src/core/engine/types';
import { RuntimeState, RuntimeEnv } from 'src/core/engine/runtime';
import { Observable } from 'rxjs';
import { RouterState } from 'src/core/engine/router/router.register';
import { UserState } from 'src/core/state/user';
import { CartState } from 'src/core/state/cart';
import { CspState } from 'src/components/CustomerServicePortal/feature/csp.register';
import { CartItemPrice, CartConfigOption } from 'src/core/state/cart';

import { paths, text } from '../config';
import { AppState } from '../features/app/app.reducer';

/**
 * AppEnv is a combination of app-specific vars +
 * the required vars from the pwa runtime
 */

export type CartProduct = {
    __typename: 'BundleProduct' | 'ConfigurableProduct' | 'SimpleProduct' | 'GroupedProduct' | 'VirtualProduct';
    small_image: {
        label: string;
        url: string;
    };
    name: string;
    sku: string;
    pathname: string;
    logo_policy?: number;
};

export type AppEnv = RuntimeEnv & {
    ALGOLIA_APP_ID: string;
    ALGOLIA_API_KEY: string;
    ALGOLIA_INDEX_PREFIX: string;
    PCA_ENABLED: boolean;
    PCA_FORM_KEY: string;
    GTM_ID: string;
    GTM_ENABLED: boolean;
    BACKEND: string;
    LOGROCKET_ID: string;
    SSR_GQL: boolean;
    SPLITIO_AUTHORIZAYION_KEY?: string;
    BUGSNAG_ID?: string;
    SMARTLOOK_ID?: string;
    RECAPTCHA_ID: string;
    IP_GEOLOCATION_API_KEY?: string;
    KLAVIYO_COMPANY_ID?: string;
    KLAVIYO_ENABLED?: boolean;
    LEADFEEDER_ENABLED?: boolean;
    GOOGLE_PAY_MERCHANT_ID?: string;
};

/**
 * AppDeps is the single global place to store things
 * you may want access in many places.
 *
 * Use this to add things to make them available in epics & components
 */
export interface AppDeps {
    paths: typeof paths;
    text: typeof text;
}

/**
 * This simply gives access to GQL queries made after SSR
 */
export type QueryMirror = { operationName: string; data: any };

/**
 * Use this type everywhere in the Application as it will give you
 * a combined object with everything visible on the runtime deps + your own
 */
export type Deps = EpicDeps<AppEnv> & AppDeps;

export interface StoreState {
    router: RouterState;
    runtime: RuntimeState;
    user: UserState;
    cart: CartState;
    app: AppState;
    csp: CspState;
}

export enum ProductType {
    BundleProduct = 'BundleProduct',
    ConfigurableProduct = 'ConfigurableProduct',
    SimpleProduct = 'SimpleProduct',
    DownloadableProduct = 'DownloadableProduct',
    GroupedProduct = 'GroupedProduct',
    VirtualProduct = 'VirtualProduct',
    WearerBundlesProduct = 'WearerBundlesProduct',
}

export enum StockStatus {
    InStock = 'InStock',
    LowStock = 'LowStock',
    OutOfStock = 'OutOfStock',
}

export type MediaEntry = {
    large_height: number;
    large_width: number;
    large_url: string;
    medium_url?: string;
    small_url?: string;
    media_type?: string;
    position: number;
    label?: string;
};

export type MediaGalleryEntry = {
    file: string;
    video_content: { video_url: string } | null;
};

export type CamelizedMediaEntry = {
    largeHeight: number;
    largeWidth: number;
    largeUrl: string;
    mediumUrl?: string;
    smallUrl?: string;
    mediaType?: string;
    position: number;
    label?: string;
};

export type ConfigurableOptions = {
    values: {
        label: string;
        value_index: number;
    }[];
};

export type ProductPrice = {
    regular_price_incl: number;
    regular_price: number;
    final_price_incl: number;
    final_price: number;
    price_incl: number;
    price: number;
    price_highest?: number;
    price_highest_incl?: number;
    previous_prices?: {
        price_exl: string;
        price_inc: string;
    };
};

export const DEFAULT_PRICE: ProductPrice = {
    regular_price_incl: 0,
    regular_price: 0,
    final_price_incl: 0,
    final_price: 0,
    price_incl: 0,
    price: 0,
};

// https://date-fns.org/v2.4.1/docs/format
export enum DateFormat {
    Order = 'd/MM/yy',
}

export type EpicReturn = Observable<{ type: string; payload?: any }>;

export type BundleErrors = {
    logoError: boolean;
    textError: boolean;
    positionError: boolean;
    emptyCustomisation: boolean;
};

export type CategoryBreadcrumbs = {
    categoryName: string;
    categoryUrl: string;
    subcategoryName: string;
    subcategoryUrl: string;
    breadcrumbLabel?: string;
};

export type TreatmentWithConfig = {
    treatment: 'on' | 'off';
};

export type CartId = {
    cartId: string;
};

export type HeaderColor =
    | 'white'
    | 'orange'
    | 'light-orange'
    | 'grey'
    | 'green'
    | 'black'
    | 'alternateColor'
    | 'red'
    | 'whiteAndOrange'
    | 'black-1000';

export type NavBackgroundColor = {
    bg?: HeaderColor;
    className?: string;
    whiteIcons?: boolean;
};

export type AlternativeSize = {
    sizeLabel: string;
    stockStatus: string;
    stockQty: number;
};

export type AlternativeSku = {
    product: {
        selectionImage: string;
        sku: string;
        priceRange: {
            minimumPrice: {
                finalPriceFormatted: string;
                finalPriceExclFormatted: string;
            };
        };
        name: string;
        image: {
            url: string;
        };
        urlKey: string;
    };
    percentSaved: number;
    alternativeSizes: AlternativeSize[];
};

type BlogPostCommon = {
    postId: number;
    title: string;
    creationTime: string;
    tags: {
        tagId: number;
        title: string;
        tagUrl: string;
    }[];
    categories: {
        categoryId: number;
        title: string;
        categoryUrl: string;
    }[];
};

export type BlogPosts = {
    blogPosts: {
        totalCount: number;
        items: ({
            featuredImage: string;
            author: {
                name: string;
            };
            identifier: string;
        } & BlogPostCommon)[];
        totalPages: number;
    };
};

export type BlogPost = {
    blogPost: {
        metaTitle: string;
        metaDescription: string;
        filteredContent: string;
        author: {
            authorId: number;
            name: string;
            authorUrl: string;
        };
        relatedProducts: string[];
    } & BlogPostCommon;
};

export enum ProductStatus {
    enable = 1,
    disable = 2,
}

export type ProductItemBySku = {
    canCustomise: boolean;
    configurableOptions?: {
        attributeCode?: string;
        id?: number;
        label?: string;
        values: {
            label?: string;
            default_label?: string;
            value_index?: number;
            swatch_data?: {
                value: string;
            };
        };
    }[];
    priceRange?: {
        minimumPrice: {
            regularPriceExclFormatted: string;
            regularPriceFormatted: string;
        };
    };
    id: number;
    name: string;
    brandBadge?: {
        label: string;
    };
    urlKey: string;
    customisationEstimates?: {
        minQty: number;
        price: number;
        priceIncl: number;
        type: number;
    }[];
    marginTierPrices: {
        discountPercentage: number;
        price: number;
        priceIncl: number;
        quantity: number;
    }[];
    sku: string;
    smallImage: {
        label: string;
        url: string;
    };
    previousPrices: {
        priceExl: string;
        priceInc: string;
    };
    variants?: {
        attributes: {
            code: string;
            label: string;
            valueIndex: number;
        }[];
        product: {
            id: number;
            onlyXLeftInStock: number;
            sku: string;
            smallImage: {
                label: string;
                url: string;
            };
            stockQty: number;
            stockStatus: string;
        };
    }[];
    numberOfColorsAvailable?: number;
    status?: ProductStatus;
    bestseller?: boolean;
    productTags: string;
    categories?: [{ id: number }];
};

type CartBundleOptionValue = {
    quantity: number;
    label: string;
    id: number;
    price: number;
    details?: {
        size: string;
        color: {
            hex?: string;
            type: string;
            url: string;
            value: string;
        };
    };
};

type CartBundleOption = {
    label: string;
    type: string;
    values: CartBundleOptionValue[];
};

export type CartItem =
    | {
          __typename: 'ConfigurableCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
          configurable_options: CartConfigOption[];
      }
    | {
          __typename: 'BundleCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
          bundle_options: CartBundleOption[];
      }
    | {
          __typename: 'SimpleCartItem';
          id: string;
          prices: CartItemPrice;
          quantity: number;
          product: CartProduct;
      };

export type BasketAddress = {
    city: string;
    country_id: string;
    customer_id: number;
    default_billing: boolean;
    default_shipping: boolean;
    firstname: string;
    id: number;
    lastname: string;
    postcode: string;
    region?: {
        region?: string;
        region_code?: string;
        region_id?: number;
    };
    region_id: number;
    street: string[];
    telephone: string;
};
